/**
 * Modal listing who's in / waitlisted / out / hasn't replied for a session.
 * Shared by the group detail screen and the home "next up" widget.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  getSessionRsvpHistory,
  getSessionRsvps,
  type RsvpEventDTO,
  type RsvpPersonDTO,
  type SessionDTO,
  type SessionRsvpBreakdownDTO,
} from "../api";

export function RsvpBreakdownModal({
  session,
  onClose,
}: {
  session: SessionDTO;
  onClose: () => void;
}) {
  const [data, setData] = useState<SessionRsvpBreakdownDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showActivity, setShowActivity] = useState(false);
  const [history, setHistory] = useState<RsvpEventDTO[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSessionRsvps(session.id)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [session.id]);

  // Lazy-load the timeline the first time it's opened.
  useEffect(() => {
    if (!showActivity || history !== null) return;
    let cancelled = false;
    getSessionRsvpHistory(session.id)
      .then((h) => !cancelled && setHistory(h))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [showActivity, history, session.id]);

  // Close on Esc
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:inset-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:max-w-md sm:mx-auto sm:my-0">
        <div className="max-w-md mx-auto px-4 pb-4 sm:pt-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] truncate">
                {session.starts_at_display}
              </div>
              <button
                onClick={onClose}
                className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              {error && (
                <div className="text-sm text-[#C84B31]">{error}</div>
              )}
              {!data && !error && (
                <div className="text-sm text-ink/55">Loading…</div>
              )}
              {data && (
                <div className="space-y-5">
                  <Bucket title="In" people={data.confirmed} kind="confirmed" />
                  {data.waitlist.length > 0 && (
                    <Bucket title="Waitlist" people={data.waitlist} kind="waitlist" />
                  )}
                  {data.declined.length > 0 && (
                    <Bucket title="Out" people={data.declined} kind="declined" />
                  )}
                  {data.no_reply.length > 0 && (
                    <Bucket title="Haven't replied" people={data.no_reply} kind="no_reply" />
                  )}

                  <div className="pt-4 border-t border-ink/10">
                    <button
                      onClick={() => setShowActivity((v) => !v)}
                      className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 hover:text-ink"
                    >
                      {showActivity ? "Hide activity ▴" : "Activity ▾"}
                    </button>
                    {showActivity && <Activity history={history} />}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Bucket({
  title,
  people,
  kind,
}: {
  title: string;
  people: RsvpPersonDTO[];
  kind: "confirmed" | "waitlist" | "declined" | "no_reply";
}) {
  const chipCls =
    kind === "confirmed" ? "bg-success text-bg"
    : kind === "declined" ? "bg-[#C84B31] text-bg"
    : kind === "waitlist" ? "bg-accent text-ink"
    : "bg-ink/10 text-ink/70";
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={"font-mono text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 " + chipCls}>
          {title}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink/45">
          {people.length}
        </span>
      </div>
      <ul className="space-y-1.5">
        {people.map((p) => (
          <li key={p.user_id} className="flex items-center gap-3">
            <div className={
              "w-7 h-7 rounded-full flex items-center justify-center font-display font-bold text-xs shrink-0 " +
              (p.is_you ? "bg-accent text-ink" : "bg-ink text-bg")
            }>
              {p.initials}
            </div>
            <div className="font-display text-sm truncate">
              {p.name}{p.is_you && (
                <span className="font-mono text-[9px] uppercase text-ink/50 ml-1 tracking-wider">you</span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// RSVP timeline — newest-first. Preserves yes→no→yes flips the roster collapses.
function Activity({ history }: { history: RsvpEventDTO[] | null }) {
  if (history === null) {
    return <div className="text-sm text-ink/45 mt-3">Loading…</div>;
  }
  if (history.length === 0) {
    return <div className="text-sm text-ink/45 mt-3">No changes yet.</div>;
  }
  return (
    <ul className="mt-3 space-y-2">
      {history.map((e) => {
        if (e.kind === "result") {
          const removed = e.event === "deleted";
          return (
            <li key={e.id} className="flex items-center gap-2.5 text-sm">
              <span
                className={"w-1.5 h-1.5 rounded-full shrink-0 " + (removed ? "bg-[#C84B31]" : "bg-ink/50")}
              />
              <span className="truncate">
                <span className="text-ink/60">{removed ? "result removed — " : "result — "}</span>
                <b className={"font-display " + (removed ? "line-through text-ink/50" : "")}>
                  {e.text}
                </b>
                {removed && e.by && <span className="text-ink/45"> by {e.by}</span>}
              </span>
              <span className="ml-auto font-mono text-[10px] text-ink/45 shrink-0">
                {new Date(e.created_at).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </li>
          );
        }
        if (e.kind === "view") {
          return (
            <li key={e.id} className="flex items-center gap-2.5 text-sm">
              <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-ink/25" />
              <span className="truncate">
                <b className="font-display">{e.is_you ? "You" : e.name}</b>{" "}
                <span className="text-ink/60">
                  viewed{e.source === "email" ? " (email)" : ""}
                </span>
              </span>
              <span className="ml-auto font-mono text-[10px] text-ink/45 shrink-0">
                {new Date(e.created_at).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </li>
          );
        }
        const verb =
          e.action === "promoted"
            ? "promoted from waitlist"
            : e.action === "no"
              ? "marked out"
              : e.status === "waitlist"
                ? "joined the waitlist"
                : "marked in";
        const dot =
          e.status === "confirmed"
            ? "bg-success"
            : e.status === "no"
              ? "bg-[#C84B31]"
              : "bg-accent";
        return (
          <li key={e.id} className="flex items-center gap-2.5 text-sm">
            <span className={"w-1.5 h-1.5 rounded-full shrink-0 " + dot} />
            <span className="truncate">
              <b className="font-display">{e.is_you ? "You" : e.name}</b>{" "}
              <span className="text-ink/60">{verb}</span>
            </span>
            <span className="ml-auto font-mono text-[10px] text-ink/45 shrink-0">
              {new Date(e.created_at).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
